import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { documentAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../components/EmployeeManagement.css';

const DocumentManagement = () => {
  const { currentUser, isPrivileged } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [form, setForm] = useState({ documentType: '', employee: '', documentName: '', file: null });

  const fetchDocs = async () => {
    try {
      setLoading(true);
      const params = filterCategory ? { documentType: filterCategory } : {};
      const res = await documentAPI.getAll(params);
      setDocuments(res?.data || res || []);
    } catch (e) { setError('Failed to load documents'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchDocs();
    if (isPrivileged) employeeAPI.getAll().then(r => setEmployees(r?.data || r || [])).catch(() => {});
  }, [filterCategory]);

  const docTypes = ['offer letter', 'experience letter', 'agreement', 'payslip', 'other'];
  const categoryCounts = docTypes.map(t => ({ type: t, count: documents.filter(d => d.documentType === t).length }));
  const categoryIcons = { 'offer letter': 'fa-file-pdf', 'experience letter': 'fa-file-alt', 'agreement': 'fa-file-contract', 'payslip': 'fa-file-invoice-dollar', 'other': 'fa-file' };

  return (
    <div className="document-management">
      <div className="module-header">
        <h2>Document Management</h2>
        {isPrivileged && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-upload"></i> Upload Document
          </button>
        )}
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {isPrivileged && (
        <div className="document-grid">
          {categoryCounts.map((cat, i) => (
            <div key={i} className="document-card" onClick={() => setFilterCategory(filterCategory === cat.type ? '' : cat.type)} style={{ cursor: 'pointer', outline: filterCategory === cat.type ? '2px solid #667eea' : 'none' }}>
              <i className={`fas ${categoryIcons[cat.type] || 'fa-file'}`}></i>
              <h4 style={{ textTransform: 'capitalize' }}>{cat.type}s</h4>
              <p>{cat.count} documents</p>
              <button className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 14px' }}>View All</button>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }}></i><p>Loading...</p></div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Document Name</th>
                <th>Type</th>
                {isPrivileged && <th>Employee</th>}
                <th>Upload Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0
                ? <tr><td colSpan="5" style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>No documents found</td></tr>
                : documents.map(doc => (
                  <tr key={doc._id}>
                    <td><i className="fas fa-file-pdf" style={{ color: '#ef4444', marginRight: 8 }}></i>{doc.documentName || doc.originalName || '—'}</td>
                    <td style={{ textTransform: 'capitalize' }}>{doc.documentType || '—'}</td>
                    {isPrivileged && <td>{doc.employee?.name || '—'}</td>}
                    <td>{doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}</td>
                    <td>
                      {doc.fileUrl && <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="btn-icon" title="Download"><i className="fas fa-download"></i></a>}
                      <button className="btn-icon" title="View"><i className="fas fa-eye"></i></button>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {isPrivileged && (
        <Modal show={showModal} onClose={() => setShowModal(false)} title="Upload Document">
          <form onSubmit={async (e) => { e.preventDefault(); setShowModal(false); fetchDocs(); }}>
            <div className="form-group">
              <label>Document Type</label>
              <select className="form-input" value={form.documentType} onChange={e => setForm({ ...form, documentType: e.target.value })} required>
                <option value="">Select Type</option>
                <option value="offer letter">Offer Letter</option>
                <option value="experience letter">Experience Letter</option>
                <option value="agreement">Agreement</option>
                <option value="payslip">Payslip</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Employee</label>
              <select className="form-input" value={form.employee} onChange={e => setForm({ ...form, employee: e.target.value })} required>
                <option value="">Select Employee</option>
                {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Document Name</label>
              <input type="text" className="form-input" placeholder="Document name" value={form.documentName} onChange={e => setForm({ ...form, documentName: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Upload File</label>
              <input type="file" className="form-input" onChange={e => setForm({ ...form, file: e.target.files[0] })} />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Upload</button>
            </div>
          </form>
        </Modal>
      )}

      <style jsx>{`
        .document-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .document-card {
          background: white;
          padding: 30px;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .document-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
        }

        .document-card i {
          font-size: 48px;
          color: var(--primary-color);
          margin-bottom: 15px;
        }

        .document-card h4 {
          margin-bottom: 10px;
          font-size: 18px;
        }

        .document-card p {
          color: var(--text-secondary);
          margin-bottom: 15px;
        }

        .text-red {
          color: var(--danger-color);
          margin-right: 8px;
        }
      `}</style>
    </div>
  );
};

export default DocumentManagement;
